import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  X,
  Loader2,
  AlertCircle,
  Image,
  Bold,
  Italic,
  Strikethrough,
  Code,
  Link,
  List,
  ListOrdered,
  Table,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Eye,
  Edit3,
  Split,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import "@/lib/monacoSetup";
import Editor from "@monaco-editor/react";

// ============= UI COMPONENTS =============
const Separator = ({ orientation = "horizontal", className = "" }) => {
  const orientationClass =
    orientation === "vertical" ? "h-full w-px" : "h-px w-full";
  return (
    <div
      className={`shrink-0 bg-(--neutral-300) ${orientationClass} ${className}`}
    />
  );
};

export const CodeEditor = ({
  value,
  onChange,
  placeholder,
  onInsert,
  onMount,
  language = "markdown",
}) => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const [theme, setTheme] = useState("vs-light");

  // Set initial base theme (will be replaced with project-specific theme
  // once Monaco mounts). We don't keep observing here because Monaco will
  // handle re-definition when dark mode changes after mount.
  useEffect(() => {
    const isDark = document.documentElement.classList.contains("dark");
    setTheme(isDark ? "vs-dark" : "vs");
  }, []);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Resolve a CSS variable to a #rrggbb hex string by briefly applying it
    // to a throw-away DOM element so the browser converts OKLCH → RGB for us.
    const cssVarToHex = (varName) => {
      const el = document.createElement("div");
      el.style.cssText = `color:var(${varName});position:fixed;pointer-events:none;opacity:0`;
      document.body.appendChild(el);
      const rgb = getComputedStyle(el).color;
      document.body.removeChild(el);
      const m = rgb.match(/(\d+),\s*(\d+),\s*(\d+)/);
      if (!m) return null;
      return (
        "#" +
        [m[1], m[2], m[3]]
          .map((v) => parseInt(v).toString(16).padStart(2, "0"))
          .join("")
      );
    };

    const defineAndApplyTheme = () => {
      const isDark = document.documentElement.classList.contains("dark");
      const base = isDark ? "vs-dark" : "vs";
      const themeName = isDark ? "zindua-project-dark" : "zindua-project-light";

      const bg =
        cssVarToHex("--surface-panel") ?? (isDark ? "#171717" : "#F5F5F5");
      const fg =
        cssVarToHex("--neutral-900") ?? (isDark ? "#F5F5F5" : "#171717");
      const muted =
        cssVarToHex("--neutral-500") ?? (isDark ? "#737373" : "#737373");
      const subtle =
        cssVarToHex("--neutral-400") ?? (isDark ? "#525252" : "#A3A3A3");
      const border =
        cssVarToHex("--neutral-300") ?? (isDark ? "#3A3A3A" : "#D3D3D3");
      const gutterBg =
        cssVarToHex("--neutral-100") ?? (isDark ? "#171717" : "#F5F5F5");
      const primary = cssVarToHex("--color-primary") ?? "#fe3448";
      const accent = cssVarToHex("--color-accent") ?? "#0091AD";

      const blendHex = (hex1, hex2, t) => {
        const p = (h) => [
          parseInt(h.slice(1, 3), 16),
          parseInt(h.slice(3, 5), 16),
          parseInt(h.slice(5, 7), 16),
        ];
        const [r1, g1, b1] = p(hex1);
        const [r2, g2, b2] = p(hex2);
        return (
          "#" +
          [
            r1 + Math.round((r2 - r1) * t),
            g1 + Math.round((g2 - g1) * t),
            b1 + Math.round((b2 - b1) * t),
          ]
            .map((v) =>
              Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0"),
            )
            .join("")
        );
      };
      const selectionBg = blendHex(bg, primary, 0.15);

      try {
        monaco.editor.defineTheme(themeName, {
          base,
          inherit: true,
          rules: [
            { token: "keyword.md", foreground: primary.slice(1), bold: true },
            { token: "heading1.md", foreground: fg.slice(1), bold: true },
            { token: "heading2.md", foreground: fg.slice(1), bold: true },
            { token: "heading3.md", foreground: fg.slice(1), bold: true },
            { token: "strong.md", foreground: fg.slice(1), bold: true },
            { token: "em.md", foreground: muted.slice(1), italic: true },
            {
              token: "strong_em.md",
              foreground: fg.slice(1),
              bold: true,
              italic: true,
            },
            { token: "string.link.md", foreground: accent.slice(1) },
            { token: "variable.md", foreground: accent.slice(1) },
            { token: "link", foreground: accent.slice(1) },
            { token: "variable.source.md", foreground: accent.slice(1) },
            { token: "string.md", foreground: accent.slice(1) },
            { token: "comment.md", foreground: subtle.slice(1), italic: true },
            { token: "quote.md", foreground: subtle.slice(1), italic: true },
            { token: "keyword.symbol.md", foreground: primary.slice(1) },
            { token: "delimiter.md", foreground: subtle.slice(1) },
          ],
          colors: {
            "editor.background": bg,
            "editor.foreground": fg,
            "editorLineNumber.foreground": subtle,
            "editorLineNumber.activeForeground": fg,
            "editorGutter.background": gutterBg,
            "editor.selectionBackground": selectionBg,
            "editor.inactiveSelectionBackground": blendHex(bg, primary, 0.08),
            "editor.lineHighlightBackground": blendHex(bg, fg, 0.04),
            "editor.lineHighlightBorder": "#00000000",
            "editorCursor.foreground": primary,
            "editorWidget.background": bg,
            "editorWidget.border": border,
            "editorSuggestWidget.background": bg,
            "editorSuggestWidget.border": border,
            "editorSuggestWidget.selectedBackground": selectionBg,
            "scrollbar.shadow": "#00000000",
            "scrollbarSlider.background": blendHex(bg, fg, 0.1),
            "scrollbarSlider.hoverBackground": blendHex(bg, fg, 0.18),
            "scrollbarSlider.activeBackground": blendHex(bg, fg, 0.25),
          },
        });
        monaco.editor.setTheme(themeName);
        setTheme(themeName);
      } catch (e) {
        monaco.editor.setTheme(base);
        setTheme(base);
      }
    };

    defineAndApplyTheme();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "class") defineAndApplyTheme();
      }
    });
    observer.observe(document.documentElement, { attributes: true });
    editor._zinduaThemeObserver = observer;
    if (onMount) onMount(editor, monaco);
  };

  // Clean up observer when editor component unmounts
  useEffect(() => {
    return () => {
      const editor = editorRef.current;
      if (editor && editor._zinduaThemeObserver) {
        editor._zinduaThemeObserver.disconnect();
        delete editor._zinduaThemeObserver;
      }
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      editorRef.current.trigger("keyboard", "tab", null);
      return;
    }

    if (onInsert && (e.ctrlKey || e.metaKey)) {
      let handled = true;
      switch (e.key) {
        case "b":
          onInsert({ before: "**", after: "**", placeholder: "bold text" });
          break;
        case "i":
          onInsert({ before: "*", after: "*", placeholder: "italic text" });
          break;
        case "k":
          onInsert({ before: "[", after: "](url)", placeholder: "link text" });
          break;
        case "l":
          onInsert({ before: "- ", after: "", placeholder: "list item" });
          break;
        default:
          handled = false;
      }
      if (handled) e.preventDefault();
    }

    if (onInsert && e.altKey) {
      let handled = true;
      switch (e.key) {
        case "s":
          onInsert({ before: "~~", after: "~~", placeholder: "strikethrough" });
          break;
        case "e":
          onInsert({ before: "`", after: "`", placeholder: "code" });
          break;
        case "o":
          onInsert({ before: "1. ", after: "", placeholder: "numbered item" });
          break;
        case "q":
          onInsert({ before: "> ", after: "", placeholder: "blockquote" });
          break;
        case "1":
          onInsert({ before: "# ", after: "", placeholder: "Heading 1" });
          break;
        case "2":
          onInsert({ before: "## ", after: "", placeholder: "Heading 2" });
          break;
        case "3":
          onInsert({ before: "### ", after: "", placeholder: "Heading 3" });
          break;
        default:
          handled = false;
      }
      if (handled) e.preventDefault();
    }
  };

  return (
    <div className="relative h-full">
      <Editor
        height="100%"
        language={language}
        theme={theme}
        value={value || ""}
        onChange={onChange}
        onMount={handleEditorDidMount}
        options={{
          minimap: { enabled: false },
          wordWrap: "on",
          fontSize: 15,
          lineHeight: 26,
          fontFamily: "'Inter', 'Open Sans', system-ui, sans-serif",
          fontLigatures: true,
          letterSpacing: 0.2,
          lineNumbers: "on",
          glyphMargin: false,
          folding: false,
          lineDecorationsWidth: 4,
          lineNumbersMinChars: 3,
          scrollBeyondLastLine: false,
          automaticLayout: true,
          padding: { top: 20, bottom: 20 },
          quickSuggestions: false,
          renderLineHighlight: "line",
          smoothScrolling: true,
          cursorBlinking: "smooth",
          cursorSmoothCaretAnimation: "on",
          contextmenu: false,
        }}
      />
    </div>
  );
};

export const Toolbar = ({
  onInsert,
  viewMode,
  setViewMode,
  onImageUpload,
  isUploading,
}) => {
  const fileInputRef = useRef(null);

  const formatButtons = [
    {
      icon: Bold,
      before: "**",
      after: "**",
      placeholder: "bold text",
      title: "Bold (Ctrl+B)",
    },
    {
      icon: Italic,
      before: "*",
      after: "*",
      placeholder: "",
      title: "Italic (Ctrl+I)",
    },
    {
      icon: Strikethrough,
      before: "~~",
      after: "~~",
      placeholder: "",
      title: "Strikethrough",
    },
    {
      icon: Code,
      before: "`",
      after: "`",
      placeholder: "code",
      title: "Inline Code",
    },
  ];

  const headerButtons = [
    {
      icon: Heading2,
      before: "## ",
      after: "",
      placeholder: " ",
      title: "Heading 2",
    },
    {
      icon: Heading3,
      before: "### ",
      after: "",
      placeholder: "",
      title: "Heading 3",
    },
    {
      icon: Heading4,
      before: "#### ",
      after: "",
      placeholder: " ",
      title: "Heading 4",
    },
    {
      icon: Heading5,
      before: "### ",
      after: "",
      placeholder: " ",
      title: "Heading 5",
    },
  ];

  const contentButtons = [
    {
      icon: Link,
      before: "[",
      after: "](url)",
      placeholder: "link",
      title: "Link",
    },
    {
      icon: List,
      before: "- ",
      after: "",
      placeholder: " ",
      title: "Bullet List",
    },
    {
      icon: ListOrdered,
      before: "1. ",
      after: "",
      placeholder: " ",
      title: "Numbered List",
    },
    {
      icon: Quote,
      before: "> ",
      after: "",
      placeholder: " ",
      title: "Quote",
    },
    {
      icon: Table,
      action:
        "|  |  |\n|----------|----------|\n|    |    |",
      title: "Table",
    },
  ];

  return (
    <div className="flex items-center gap-2 p-3 border-b border-(--neutral-300) overflow-x-auto">
      <div className="flex items-center gap-1">
        {formatButtons.map((btn, i) => (
          <Button
            key={i}
            variant="ghost"
            size="lg"
            onClick={() => onInsert(btn)}
            title={btn.title}
            className="h-10 w-10 p-0"
          >
            <btn.icon className="h-4 w-4" />
          </Button>
        ))}
      </div>

      <Separator orientation="vertical" className="mx-2 h-6" />

      <div className="flex items-center gap-1">
        {headerButtons.map((btn, i) => (
          <Button
            key={i}
            variant="ghost"
            size="lg"
            onClick={() => onInsert(btn)}
            title={btn.title}
            className="h-10 w-10 p-0"
          >
            <btn.icon className="h-4 w-4" />
          </Button>
        ))}
      </div>

      <Separator orientation="vertical" className="mx-2 h-6" />

      <div className="flex items-center gap-1">
        {contentButtons.map((btn, i) => (
          <Button
            key={i}
            variant="ghost"
            size="lg"
            onClick={() => onInsert(btn.action ? { action: btn.action } : btn)}
            title={btn.title}
            className="h-10 w-10 p-0"
          >
            <btn.icon className="h-4 w-4" />
          </Button>
        ))}

        <Button
          variant="ghost"
          size="lg"
          onClick={() => fileInputRef.current?.click()}
          title="Upload Image to R2"
          className="h-10 w-10 p-0"
          disabled={isUploading}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin text-(--color-accent)" />
          ) : (
            <Image className="h-4 w-4" />
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
          multiple
          onChange={onImageUpload}
          className="hidden"
        />
      </div>

      <Separator orientation="vertical" className="mx-2 h-6" />

      <div className="flex items-center gap-1">
        <Button
          variant={viewMode === "edit" ? "default" : "ghost"}
          size="lg"
          onClick={() => setViewMode("edit")}
          title="Edit Mode"
          className="h-8 w-8 p-0"
        >
          <Edit3 className="h-4 w-4" />
        </Button>
        <Button
          variant={viewMode === "split" ? "default" : "ghost"}
          size="lg"
          onClick={() => setViewMode("split")}
          title="Split Mode"
          className="h-8 w-8 p-0"
        >
          <Split className="h-4 w-4" />
        </Button>
        <Button
          variant={viewMode === "preview" ? "default" : "ghost"}
          size="lg"
          onClick={() => setViewMode("preview")}
          title="Preview Mode"
          className="h-8 w-8 p-0"
        >
          <Eye className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Simple markdown renderer for preview
const SimpleMarkdownPreview = ({ content }) => {
  return (
    <div className="prose dark:prose-invert max-w-none">
      <pre className="whitespace-pre-wrap">{content}</pre>
    </div>
  );
};
